import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import AudioListen from "@/components/AudioListen";

const chapters = [
  {
    n: "01",
    title: "The Weight is Real.",
    body: "The worker arrives with more than a schedule. Grief. Anger. The residue of yesterday's shift. To pretend otherwise is corporate malpractice.",
  },
  {
    n: "02",
    title: "Healing is a Pre-Requisite.",
    body: "You would not send a soldier to the field without armor. Yet we send workers to hard days with nothing but coffee and command.",
  },
  {
    n: "03",
    title: "Change is Earned in Adversity.",
    body: "Comfort teaches nothing. The eight modules exist because the transformation must be forged, not delivered.",
  },
  {
    n: "04",
    title: "The Company is Complicit — or Curative.",
    body: "There is no neutral ground. Either your culture heals your people, or it costs you them.",
  },
];

const chapterVariant = {
  hidden: { opacity: 0, y: 40 },
  visible: (i) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.9, delay: i * 0.05, ease: [0.65, 0, 0.35, 1] },
  }),
};

export default function Manifesto() {
  return (
    <section id="manifesto" className="relative bg-[#FAF7F2] py-28 md:py-40">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-y-16 mb-20">
          <div className="col-span-12 md:col-span-4">
            <div className="section-tag mb-6">§ Chapter Zero</div>
            <h2 className="font-display text-5xl md:text-6xl leading-[0.95] tracking-tight">
              The <span className="italic text-[#B45309]">Manifesto</span>.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-7 md:col-start-6 flex items-end">
            <p className="text-[#44403C] text-lg md:text-xl leading-relaxed max-w-2xl">
              Four principles that anchor every module, every book, every corporate engagement. Read them slowly. They are the whole point.
            </p>
          </div>
        </div>

        <div className="divide-y divide-white/10 border-t border-[#E7DFD5]">
          {chapters.map((c, i) => (
            <motion.article
              key={c.n}
              custom={i}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={chapterVariant}
              className="grid grid-cols-12 gap-6 md:gap-10 py-12 md:py-20 group"
            >
              <div className="col-span-12 md:col-span-3">
                <div className="chapter-number text-8xl md:text-9xl text-[#44403C] group-hover:text-[#B45309] transition-colors duration-700">
                  {c.n}
                </div>
              </div>
              <div className="col-span-12 md:col-span-6 md:col-start-4">
                <h3 className="font-display text-3xl md:text-5xl leading-tight tracking-tight mb-6">
                  {c.title}
                </h3>
                <p className="text-[#78716C] text-lg leading-relaxed max-w-xl mb-6">{c.body}</p>
                <AudioListen text={`Chapter ${c.n}. ${c.title} ${c.body}`} label="Listen to this chapter" testid={`manifesto-listen-${c.n}`} />
              </div>
              <div className="col-span-12 md:col-span-2 md:col-start-11 flex md:justify-end items-start">
                <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#78716C]">/{c.n}</span>
              </div>
            </motion.article>
          ))}
        </div>

        <div className="mt-16 flex flex-wrap gap-4">
          <Link to="/modules" className="btn-ember text-sm">Enter the 8 Modules</Link>
          <Link to="/books" className="btn-outline text-sm">Read the Books</Link>
        </div>
      </div>
    </section>
  );
}
